import * as React from "react";

import { createPreviewData } from "@v1/actions/core-actions";
import {
  errorToast,
  successToast,
} from "@v1/components/designSystem/alert/Toast";
import DeprecatedIconButton from "@v1/components/designSystem/button/IconButton";
import PreviewPageButton from "@v1/components/header/PreviewPageButton";
import { PublishPageButton } from "@v1/components/header/PublishPageButton";
import { PublishPageButtonWithCallout } from "@v1/components/header/PublishPageButtonWithCallout";
import { useExperimentCreate } from "@v1/features/experiments/tabs/hooks/useExperimentCreate";
import { generateExperimentVariation } from "@v1/features/experiments/utils";
import useGetStoreNameAndUrl from "@v1/hooks/useGetStoreNameAndUrl";
import usePublishedInfo from "@v1/hooks/usePublishedInfo";
import { selectDraftElementType } from "@v1/reducers/core-reducer";
import { selectTemplateEditorStoreProduct } from "@v1/reducers/template-reducer";
import { selectIsShopifyStoreClosed } from "@v1/reducers/ui-reducer";
import { useEditorSelector, useEditorStore } from "@v1/store/store";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import { useCurrentWorkspaceContext } from "@v2/contexts/WorkspaceDashboardContext";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { useModal } from "@v2/store/modalStore";
import { getStoreData } from "@v2/utils/project-utils";
import { ExternalLink, FlaskConical, Users } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";

export default function PageControlButtons() {
  const modal = useModal();
  const draftElementType = useEditorSelector(selectDraftElementType);
  const { project } = useCurrentProjectContext();
  const store = getStoreData(project);
  const isStoreClosed = useEditorSelector(selectIsShopifyStoreClosed);
  const reduxStore = useEditorStore();
  const { storeUrl } = useGetStoreNameAndUrl();
  const projectId = useCurrentProjectId();
  const currentProduct = useEditorSelector(selectTemplateEditorStoreProduct);

  const logEvent = useLogAnalytics();
  const { shopifyUrl, urlIsDisabled, tooltipLabel, customDomainUrl } =
    usePublishedInfo();

  const { workspaceId, isLoading: isWorkspaceLoading } =
    useCurrentWorkspaceContext();
  const { createExperiment, isCreatingExperiment } = useExperimentCreate({
    workspaceId: workspaceId ?? undefined,
    projectId,
  });

  if (!draftElementType) {
    return null;
  }

  const showABTestButton =
    draftElementType === "page" ||
    draftElementType === "shopifyProductTemplate";

  const handleStartABTest = async () => {
    if (!shopifyUrl) {
      errorToast(
        "Error creating test",
        "Please try again, or contact support@replo.app for help.",
      );
      return;
    }

    await createExperiment({
      location: "editor_header",
      partialExperiment: {
        variations: [
          generateExperimentVariation({
            slug: "Variant-A",
            target: customDomainUrl ?? shopifyUrl,
          }),
          generateExperimentVariation({
            slug: "Variant-B",
            target: null,
          }),
        ],
      },
      onExperimentCreateSuccess: () => {
        successToast(
          "Test Created",
          "Select one other page and choose a link to start",
        );
      },
    });
  };

  const productNotAvailableOnProductTemplate =
    draftElementType === "shopifyProductTemplate" && !currentProduct?.handle;

  return (
    <div className="flex items-center gap-2">
      <DeprecatedIconButton
        variant="secondary"
        size="base"
        tooltipText="Share Project"
        icon={<Users size={16} />}
        onClick={() => {
          modal.openModal({
            type: "projectMembershipModal",
          });
        }}
      />
      <div className="bg-border h-[16px] w-[1px]" />
      {showABTestButton && (
        <DeprecatedIconButton
          variant="secondary"
          size="base"
          icon={<FlaskConical size={16} />}
          onClick={() => void handleStartABTest()}
          disabled={
            !shopifyUrl ||
            !customDomainUrl ||
            isCreatingExperiment ||
            isWorkspaceLoading ||
            productNotAvailableOnProductTemplate
          }
          tooltipText={
            shopifyUrl ? "Run A/B test" : "Publish page to run A/B test"
          }
        />
      )}
      <PreviewPageButton
        isProjectConnectedToShopify={Boolean(store?.shopifyUrl)}
        onCreatePreviewData={() => {
          if (!projectId || !storeUrl) {
            return;
          }
          return createPreviewData({
            state: reduxStore.getState(),
            projectId,
            storeUrl,
          });
        }}
        isStoreClosed={isStoreClosed}
      />
      <AnimatePresence>
        {!urlIsDisabled && (
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: "auto", opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <DeprecatedIconButton
              variant="secondary"
              data-testid="live-page-button"
              size="base"
              disabled={urlIsDisabled}
              icon={<ExternalLink size={16} />}
              tooltipText={tooltipLabel}
              aria-label={tooltipLabel}
              onClick={() => {
                if (shopifyUrl) {
                  logEvent("header.viewPage", {});
                  window.open(shopifyUrl, "_blank");
                }
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
      {store?.shopifyUrl ? (
        <PublishPageButton isPreviewBeforePublishView />
      ) : (
        <PublishPageButtonWithCallout />
      )}
    </div>
  );
}
